// @ts-nocheck
import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Mail, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  event: any;
  eventId: string | undefined;
}

type StatusFilter = "all" | "waiting" | "offered" | "accepted" | "declined" | "expired";

const STATUS_COLORS: Record<string, string> = {
  waiting: "bg-amber-100 text-amber-800",
  offered: "bg-blue-100 text-blue-800",
  accepted: "bg-green-100 text-green-800",
  declined: "bg-gray-100 text-gray-700",
  expired: "bg-red-100 text-red-700",
};

export function WaitlistSection({ event, eventId }: Props) {
  const [entries, setEntries] = useState<any[]>([]);
  const [divisions, setDivisions] = useState<any[]>([]);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("all");
  const [filterDivisionId, setFilterDivisionId] = useState<string>("all");
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Add form
  const [newEntry, setNewEntry] = useState({
    team_name: "",
    catalog_division_id: "",
    gender: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
  });

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    const [{ data: w }, { data: divs }] = await Promise.all([
      supabase
        .from("event_waitlist")
        .select("*")
        .eq("event_id", eventId)
        .order("position", { ascending: true })
        .order("created_at", { ascending: true }),
      supabase
        .from("event_division_settings")
        .select("id, catalog_division_id, division_catalog(label)")
        .eq("event_id", eventId),
    ]);
    setEntries(w ?? []);
    setDivisions(divs ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterStatus !== "all" && e.status !== filterStatus) return false;
      if (filterDivisionId !== "all" && e.catalog_division_id !== filterDivisionId) return false;
      return true;
    });
  }, [entries, filterStatus, filterDivisionId]);

  const updateStatus = async (id: string, updates: any) => {
    const { error } = await supabase.from("event_waitlist").update(updates).eq("id", id);
    if (error) {
      toast.error("Failed to update");
      return;
    }
    toast.success("Updated");
    load();
  };

  const offerSpot = (id: string) => {
    const now = new Date();
    const expires = new Date(now.getTime() + 48 * 60 * 60 * 1000);
    updateStatus(id, {
      status: "offered",
      offered_at: now.toISOString(),
      offer_expires_at: expires.toISOString(),
    });
  };

  const removeEntry = async (id: string) => {
    if (!confirm("Remove this waitlist entry?")) return;
    const { error } = await supabase.from("event_waitlist").delete().eq("id", id);
    if (error) {
      toast.error("Failed to remove");
      return;
    }
    toast.success("Removed");
    load();
  };

  const addEntry = async () => {
    if (!eventId) return;
    if (!newEntry.team_name.trim() || !newEntry.contact_name.trim() || !newEntry.contact_email.trim()) {
      toast.error("Team name, contact name, and email are required");
      return;
    }
    const maxPos = entries.reduce((max, e) => Math.max(max, e.position ?? 0), 0);
    const { error } = await supabase.from("event_waitlist").insert({
      event_id: eventId,
      team_name: newEntry.team_name.trim(),
      catalog_division_id: newEntry.catalog_division_id || null,
      gender: newEntry.gender || null,
      contact_name: newEntry.contact_name.trim(),
      contact_email: newEntry.contact_email.trim().toLowerCase(),
      contact_phone: newEntry.contact_phone || null,
      notes: newEntry.notes || null,
      position: maxPos + 1,
      status: "waiting",
    });
    if (error) {
      toast.error("Failed to add: " + error.message);
      return;
    }
    toast.success("Added to waitlist");
    setAddOpen(false);
    setNewEntry({
      team_name: "",
      catalog_division_id: "",
      gender: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      notes: "",
    });
    load();
  };

  const divisionLabel = (cdId: string | null | undefined) => {
    if (!cdId) return "—";
    const ds = divisions.find((d) => d.catalog_division_id === cdId);
    return ds?.division_catalog?.label ?? "—";
  };

  if (!eventId) {
    return (
      <SubSection title="Waitlist">
        <p className="text-sm text-muted-foreground">Save the event first to manage its waitlist.</p>
      </SubSection>
    );
  }

  return (
    <div className="space-y-6">
      <SubSection title={`Waitlist for ${event.name || "this event"}`}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-muted-foreground">
            {entries.length} {entries.length === 1 ? "person" : "people"} on the waitlist
          </p>
          <Button onClick={() => setAddOpen(true)} size="sm">
            <Plus className="w-4 h-4 mr-1" /> Manually Add
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {(["all", "waiting", "offered", "accepted", "declined", "expired"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={cn(
                "px-3 py-1 text-xs rounded-full border capitalize",
                filterStatus === s
                  ? "bg-primary text-white border-primary"
                  : "bg-background border-border hover:border-primary"
              )}
            >
              {s}
            </button>
          ))}
          <select
            value={filterDivisionId}
            onChange={(e) => setFilterDivisionId(e.target.value)}
            className="text-xs border border-input rounded px-2 py-1 ml-auto"
          >
            <option value="all">All divisions</option>
            {divisions.map((d) => (
              <option key={d.id} value={d.catalog_division_id}>
                {d.division_catalog?.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
            {entries.length === 0 ? "No one on the waitlist yet." : "No entries match the filters."}
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Team</th>
                  <th className="px-3 py-2 text-left">Division</th>
                  <th className="px-3 py-2 text-left">Gender</th>
                  <th className="px-3 py-2 text-left">Contact</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((e) => (
                  <tr key={e.id}>
                    <td className="px-3 py-2">{e.position ?? "—"}</td>
                    <td className="px-3 py-2 font-medium">{e.team_name}</td>
                    <td className="px-3 py-2">{divisionLabel(e.catalog_division_id)}</td>
                    <td className="px-3 py-2 capitalize">{e.gender ?? "—"}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs">{e.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{e.contact_email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={cn("px-2 py-0.5 rounded text-xs capitalize", STATUS_COLORS[e.status])}>
                        {e.status}
                      </span>
                      {e.status === "offered" && e.offer_expires_at && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          Expires {new Date(e.offer_expires_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {e.status === "waiting" && (
                          <button
                            onClick={() => offerSpot(e.id)}
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            title="Offer spot"
                          >
                            <Mail className="w-3 h-3 inline" /> Offer
                          </button>
                        )}
                        {e.status === "offered" && (
                          <>
                            <button
                              onClick={() =>
                                updateStatus(e.id, { status: "accepted", responded_at: new Date().toISOString() })
                              }
                              className="text-xs p-1 text-green-700 hover:bg-green-50 rounded"
                              title="Mark accepted"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                updateStatus(e.id, { status: "declined", responded_at: new Date().toISOString() })
                              }
                              className="text-xs p-1 text-red-700 hover:bg-red-50 rounded"
                              title="Mark declined"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => removeEntry(e.id)}
                          className="text-xs p-1 text-muted-foreground hover:text-red-700 hover:bg-red-50 rounded"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SubSection>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Waitlist</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <FieldLabel>Team Name *</FieldLabel>
              <Input
                value={newEntry.team_name}
                onChange={(e) => setNewEntry({ ...newEntry, team_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Division</FieldLabel>
                <select
                  value={newEntry.catalog_division_id}
                  onChange={(e) => setNewEntry({ ...newEntry, catalog_division_id: e.target.value })}
                  className={inputClass}
                >
                  <option value="">—</option>
                  {divisions.map((d) => (
                    <option key={d.id} value={d.catalog_division_id}>
                      {d.division_catalog?.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Gender</FieldLabel>
                <select
                  value={newEntry.gender}
                  onChange={(e) => setNewEntry({ ...newEntry, gender: e.target.value })}
                  className={inputClass}
                >
                  <option value="">—</option>
                  <option value="boys">Boys</option>
                  <option value="girls">Girls</option>
                </select>
              </div>
            </div>
            <div>
              <FieldLabel>Contact Name *</FieldLabel>
              <Input
                value={newEntry.contact_name}
                onChange={(e) => setNewEntry({ ...newEntry, contact_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Email *</FieldLabel>
                <Input
                  type="email"
                  value={newEntry.contact_email}
                  onChange={(e) => setNewEntry({ ...newEntry, contact_email: e.target.value })}
                />
              </div>
              <div>
                <FieldLabel>Phone</FieldLabel>
                <Input
                  value={newEntry.contact_phone}
                  onChange={(e) => setNewEntry({ ...newEntry, contact_phone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <FieldLabel>Notes</FieldLabel>
              <Textarea
                rows={2}
                value={newEntry.notes}
                onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addEntry}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default WaitlistSection;
